if (!customElements.get('sidebar-drawer')) {
  class SidebarDrawer extends HTMLElement {
    static get observedAttributes() {
      return ['open'];
    }

    constructor() {
      super();
      this.isOpen = false;
      this.returnFocusTo = null;
      this.openedBy = null;
    }

    open() {
      this.toggleState(true);
    }

    close() {
      this.toggleState(false);
    }

    toggleState(isOpen) {
      if (isOpen === this.isOpen) return;
      this.isOpen = isOpen;

      if (isOpen) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
      }
      document.body.classList.toggle('no-scroll', isOpen);
      const method = isOpen ? 'add' : 'remove';
      this.classList[method]('!translate-x-0', '!opacity-100', 'open');
      this.inert = !isOpen;

      const overlay = this.closest('.drawer-container');
      if (overlay) {
        overlay.classList[method]('open');
      }

      this.syncTriggers(isOpen);

      if (isOpen) {
        this.returnFocusTo = this.openedBy || document.activeElement;
        this.openedBy = null;
        document.addEventListener('keydown', this.handleTab);
        document.addEventListener('focusin', this.handleFocusIn);
        this.focusFirstElement();
      } else {
        document.removeEventListener('keydown', this.handleTab);
        document.removeEventListener('focusin', this.handleFocusIn);
        this.returnFocusTo?.focus?.();
        this.returnFocusTo = null;
      }
    }

    syncTriggers(isOpen) {
      if (!this.id) return;
      document
        .querySelectorAll(`[aria-controls="${CSS.escape(this.id)}"]`)
        .forEach((trigger) => {
          trigger.setAttribute('aria-expanded', String(isOpen));
          trigger.toggleAttribute('is-open', isOpen);
        });
    }

    focusFirstElement() {
      const [first] = this.getFocusable();
      (first || this).focus({ preventScroll: true });
    }

    getFocusable() {
      return getFocusableElements(this).filter(
        (element) => !element.closest('[inert]') && element.getClientRects().length > 0
      );
    }

    handleTab = (event) => {
      if (event.key !== 'Tab') return;

      const focusable = this.getFocusable();
      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !this.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !this.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };

    handleFocusIn = (event) => {
      if (this.isOpen && !this.contains(event.target)) {
        this.focusFirstElement();
      }
    };

    handleEscape = (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.close();
      }
    };

    handleOverlayClick = (event) => {
      if (event.target === event.currentTarget) {
        this.close();
      }
    };

    handleClick = (event) => {
      const trigger = event.target.closest('[aria-controls]');
      if (trigger && this.id && trigger.getAttribute('aria-controls') === this.id) {
        this.openedBy = trigger;
        this.open();
        return;
      }

      if (this.contains(event.target) && event.target.closest('[data-drawer-close]')) {
        this.close();
      }
    };

    connectedCallback() {
      this.inert = !this.isOpen;
      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '-1');

      if (this.hasAttribute('open')) {
        this.open();
      }

      this.overlay = this.closest('.drawer-container');
      this.overlay?.addEventListener('click', this.handleOverlayClick);

      document.addEventListener('click', this.handleClick);
      document.addEventListener('keyup', this.handleEscape);

      this.detachSwipeClose = window.attachDrawerSwipeClose?.(this, {
        position: () => this.dataset.drawerPosition || 'right',
        onClose: () => this.close(),
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleClick);
      document.removeEventListener('keyup', this.handleEscape);
      document.removeEventListener('keydown', this.handleTab);
      document.removeEventListener('focusin', this.handleFocusIn);
      this.overlay?.removeEventListener('click', this.handleOverlayClick);
      this.detachSwipeClose?.();

      if (this.isOpen) document.body.classList.remove('no-scroll');
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === 'open' && this.isConnected) {
        if (newValue !== null) {
          this.open();
        } else {
          this.close();
        }
      }
    }
  }

  customElements.define('sidebar-drawer', SidebarDrawer);
}
